/**
 * Helper utilities for working with competitive intelligence data (spyfu_data).
 *
 * Since spyfu_data is already nested in business objects from /reviews/ endpoint,
 * these utilities make it easier to extract and work with the data.
 */

/**
 * Check if a business has competitive intelligence data
 * @param {Object} business - Business object from API
 * @returns {boolean} - True if business has spyfu_data
 */
export const hasCompetitiveIntel = (business) => {
  return business?.spyfu_data && Object.keys(business.spyfu_data).length > 0;
};

/**
 * Get domain stats for a business
 * @param {Object} business - Business object with spyfu_data
 * @returns {Object|null} - Domain stats or null
 */
export const getDomainStats = (business) => {
  if (!hasCompetitiveIntel(business)) return null;

  const stats = business.spyfu_data?.domain_stats;
  if (!stats?.results?.length) return null;

  // Return the most recent stats (first result)
  return stats.results[0];
};

/**
 * Get domain strength score (0-100)
 * @param {Object} business - Business object with spyfu_data
 * @returns {number} - Strength score or 0
 */
export const getDomainStrength = (business) => {
  const stats = getDomainStats(business);
  return stats?.strength || 0;
};

/**
 * Get monthly organic clicks
 * @param {Object} business - Business object with spyfu_data
 * @returns {number} - Monthly organic clicks or 0
 */
export const getMonthlyOrganicClicks = (business) => {
  const stats = getDomainStats(business);
  return stats?.monthlyOrganicClicks || 0;
};

/**
 * Get traffic value from multiple sources for consistent display
 * Checks all available data sources in priority order:
 * 1. SpyFu domain stats (monthlyOrganicClicks)
 * 2. Traffic data field (estimated_monthly_visitors) 
 * 3. Historical traffic time series (latest month's organicClicks)
 * @param {Object} business - Business object with spyfu_data and/or traffic_data
 * @param {Object} trafficHistory - Map of maps_uri -> traffic chart data array (optional)
 * @returns {number|null} - Traffic value or null if no data available
 */
export const getTrafficValue = (business, trafficHistory = null) => {
  // First, try SpyFu data (most authoritative current value)
  if (hasCompetitiveIntel(business)) {
    const clicks = getMonthlyOrganicClicks(business);
    if (clicks > 0) {
      return clicks;
    }
  }
  
  // Second, try traffic_data field (used by KeywordIntelligenceDashboard)
  const trafficDataVisitors = business?.traffic_data?.metrics?.estimated_monthly_visitors;
  if (trafficDataVisitors && trafficDataVisitors > 0) {
    return trafficDataVisitors;
  }
  
  // Third, try to get the latest value from historical traffic data
  if (trafficHistory && business?.maps_uri) {
    const historyData = trafficHistory[business.maps_uri];
    if (historyData && Array.isArray(historyData) && historyData.length > 0) {
      // Get the latest month's value (last item in the array)
      const latestMonth = historyData[historyData.length - 1];
      const clicks = latestMonth?.organicClicks;
      if (clicks && clicks > 0) {
        return clicks;
      }
    }
  }
  
  return null;
};

/**
 * Format traffic value for display with consistent format
 * @param {Object} business - Business object with spyfu_data
 * @param {Object} trafficHistory - Map of maps_uri -> traffic chart data array (optional)
 * @param {boolean} includeSuffix - Whether to include "/mo" suffix (default: true)
 * @returns {string} - Formatted traffic string or 'N/A'
 */
export const formatTrafficDisplay = (business, trafficHistory = null, includeSuffix = true) => {
  const traffic = getTrafficValue(business, trafficHistory);
  if (traffic === null || traffic === 0) {
    return 'N/A';
  }
  const formatted = formatNumber(traffic);
  return includeSuffix ? `${formatted}/mo` : formatted;
};

/**
 * Get monthly organic value (estimated $ value)
 * @param {Object} business - Business object with spyfu_data
 * @returns {number} - Monthly organic value or 0
 */
export const getMonthlyOrganicValue = (business) => {
  const stats = getDomainStats(business);
  return stats?.monthlyOrganicValue || 0;
};

/**
 * Get total organic keywords ranking
 * @param {Object} business - Business object with spyfu_data
 * @returns {number} - Total organic keywords or 0
 */
export const getTotalOrganicKeywords = (business) => {
  const stats = getDomainStats(business);
  return stats?.totalOrganicResults || 0;
};

/**
 * Get SEO keywords for a business
 * @param {Object} business - Business object with spyfu_data
 * @param {number} limit - Maximum number of keywords to return
 * @returns {Array} - Array of keyword objects
 */
export const getSEOKeywords = (business, limit = 50) => {
  if (!hasCompetitiveIntel(business)) return [];

  const keywords = business.spyfu_data?.seo_keywords?.results || [];
  return keywords.slice(0, limit);
};

/**
 * Get top N SEO keywords sorted by search volume
 * @param {Object} business - Business object with spyfu_data
 * @param {number} limit - Number of keywords to return
 * @returns {Array} - Top keywords by search volume
 */
export const getTopKeywordsByVolume = (business, limit = 10) => {
  const keywords = getSEOKeywords(business, 1000);
  return keywords
    .sort((a, b) => (b.searchVolume || 0) - (a.searchVolume || 0))
    .slice(0, limit);
};

/**
 * Get keyword opportunities (high volume, low difficulty, not ranking well)
 * @param {Object} business - Business object with spyfu_data
 * @param {Object} options - Filter options
 * @returns {Array} - Keyword opportunities
 */
export const getKeywordOpportunities = (business, options = {}) => {
  const {
    minVolume = 100,
    maxDifficulty = 50,
    minRank = 11, // Not in top 10
  } = options;

  const keywords = getSEOKeywords(business, 1000);

  return keywords
    .filter(kw =>
      kw.searchVolume >= minVolume &&
      kw.keywordDifficulty <= maxDifficulty &&
      kw.rank >= minRank
    )
    .sort((a, b) => {
      // Opportunity score: high volume / (difficulty + 1)
      const scoreA = a.searchVolume / (a.keywordDifficulty + 1);
      const scoreB = b.searchVolume / (b.keywordDifficulty + 1);
      return scoreB - scoreA;
    })
    .slice(0, 20);
};

/**
 * Get SEO competitors for a business
 * @param {Object} business - Business object with spyfu_data
 * @returns {Array} - Array of competitor objects
 */
export const getSEOCompetitors = (business) => {
  if (!hasCompetitiveIntel(business)) return [];
  return business.spyfu_data?.seo_competitors?.results || [];
};

/**
 * Get PPC competitors for a business
 * @param {Object} business - Business object with spyfu_data
 * @returns {Array} - Array of PPC competitor objects
 */
export const getPPCCompetitors = (business) => {
  if (!hasCompetitiveIntel(business)) return [];
  return business.spyfu_data?.ppc_competitors?.results || [];
};

/**
 * Get paid search campaigns (PPC)
 * @param {Object} business - Business object with spyfu_data
 * @returns {Array} - Array of paid campaign objects
 */
export const getPaidCampaigns = (business) => {
  if (!hasCompetitiveIntel(business)) return [];
  return business.spyfu_data?.paid_serps?.results || [];
};

/**
 * Get monthly paid clicks
 * @param {Object} business - Business object with spyfu_data
 * @returns {number} - Monthly paid clicks or 0
 */
export const getMonthlyPaidClicks = (business) => {
  const stats = getDomainStats(business);
  return stats?.monthlyPaidClicks || 0;
};

/**
 * Get total paid keywords (ads purchased)
 * @param {Object} business - Business object with spyfu_data
 * @returns {number} - Total paid keywords or 0
 */
export const getTotalPaidKeywords = (business) => {
  const stats = getDomainStats(business);
  return stats?.totalAdsPurchased || 0;
};

/**
 * Get average organic rank
 * @param {Object} business - Business object with spyfu_data
 * @returns {number} - Average organic rank or 0
 */
export const getAverageOrganicRank = (business) => {
  const stats = getDomainStats(business);
  return stats?.averageOrganicRank || 0;
};

/**
 * Get monthly PPC budget estimate
 * @param {Object} business - Business object with spyfu_data
 * @returns {number} - Estimated monthly PPC budget or 0
 */
export const getMonthlyPPCBudget = (business) => {
  const stats = getDomainStats(business);
  return stats?.monthlyBudget || 0;
};

/**
 * Calculate SEO opportunity score for a business
 * Higher score = better opportunity (high value keywords not yet ranking well)
 * @param {Object} business - Business object with spyfu_data
 * @returns {number} - Opportunity score
 */
export const calculateSEOOpportunityScore = (business) => {
  const opportunities = getKeywordOpportunities(business);
  if (opportunities.length === 0) return 0;

  // Sum of potential traffic from opportunities
  return opportunities.reduce((sum, kw) => {
    // Estimate potential clicks if ranked #3 (assuming 11% CTR)
    const potentialClicks = kw.searchVolume * 0.11;
    return sum + potentialClicks;
  }, 0);
};

/**
 * Get competitive intelligence summary for a business
 * @param {Object} business - Business object with spyfu_data
 * @returns {Object} - Summary object with key metrics
 */
export const getCompetitiveIntelSummary = (business) => {
  if (!hasCompetitiveIntel(business)) {
    return {
      hasData: false,
      domain: null,
      enrichedAt: null,
    };
  }

  const stats = getDomainStats(business);

  return {
    hasData: true,
    domain: business.spyfu_data.domain,
    enrichedAt: business.spyfu_data.enriched_at,
    domainStrength: getDomainStrength(business),
    monthlyOrganicClicks: getMonthlyOrganicClicks(business),
    monthlyOrganicValue: getMonthlyOrganicValue(business),
    totalKeywords: getTotalOrganicKeywords(business),
    topKeywords: getTopKeywordsByVolume(business, 5),
    seoCompetitorCount: getSEOCompetitors(business).length,
    ppcCompetitorCount: getPPCCompetitors(business).length,
    paidCampaignCount: getPaidCampaigns(business).length,
    monthlyPPCBudget: getMonthlyPPCBudget(business),
    opportunityScore: calculateSEOOpportunityScore(business),
  };
};

/**
 * Format currency for display
 * @param {number} value - Currency value
 * @returns {string} - Formatted currency string
 */
export const formatCurrency = (value) => {
  if (value === 0 || !value) return '$0';
  if (value >= 1000000) return `$${(value / 1000000).toFixed(1)}M`;
  if (value >= 1000) return `$${(value / 1000).toFixed(1)}K`;
  return `$${value.toLocaleString()}`;
};

/**
 * Format large numbers for display
 * @param {number} num - Number to format
 * @returns {string} - Formatted number string
 */
export const formatNumber = (num) => {
  if (num === 0 || !num) return '0';
  if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
  if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
  return num.toLocaleString();
};

/**
 * Get color for domain strength score
 * @param {number} strength - Strength score (0-100)
 * @returns {string} - Chakra UI color scheme
 */
export const getDomainStrengthColor = (strength) => {
  if (strength >= 70) return 'green';
  if (strength >= 50) return 'blue';
  if (strength >= 30) return 'yellow';
  if (strength >= 10) return 'orange';
  return 'red';
};

/**
 * Calculate SEO Score for a business
 * SEO Score = Monthly Visitors / Search Rank
 * This is the same calculation used in WebTrafficDashboard
 * @param {Object} business - Business object with traffic_data and query_ranking_info
 * @returns {number} - SEO Score (higher is better)
 */
export const calculateSeoScore = (business) => {
  const visitors = business?.traffic_data?.metrics?.estimated_monthly_visitors || 0;
  if (visitors === 0) return 0;

  let rankForSeo = business?.query_ranking_info?.url_ranking;

  if (typeof rankForSeo === 'number' && rankForSeo > 0 && rankForSeo <= 100) {
    return visitors / rankForSeo;
  } else {
    // Unranked, ranked > 100, or invalid rank: use 100 as divisor
    return visitors / 100;
  }
};

/**
 * Get color for SEO Score
 * @param {number} score - SEO Score value
 * @returns {string} - Chakra UI color
 */
export const getSeoScoreColor = (score) => {
  if (score >= 100) return 'green.600';
  if (score >= 50) return 'blue.600';
  if (score >= 20) return 'orange.500';
  return 'gray.600';
};

// ============================================================================
// SEO SCORE ESTIMATION
// ============================================================================
//
// When SpyFu hasn't enriched a business yet, its domain strength and the
// visitors/rank-based SEO score are unavailable. Rather than showing "N/A"
// — which is unhelpful for the user — we derive a reasonable estimate from
// signals that ARE available on every business: search-rank position,
// review volume, average rating, and any traffic data we do have. These
// estimates are deterministic, bounded, and conservative (they never claim
// "real" SpyFu numbers — callers can flag them with `isEstimated*` helpers
// to surface a visual "est." hint to the user).

const _clamp = (value, min, max) => Math.min(max, Math.max(min, value));

const _getRank = (business) => {
  const rank = business?.query_ranking_info?.url_ranking;
  if (typeof rank === 'number' && rank > 0) return rank;
  return null;
};

const _getReviewCount = (business) =>
  Number(business?.rating_count ?? business?.total_reviews ?? 0) || 0;

const _getAvgRating = (business) =>
  Number(business?.rating_avg ?? business?.averageRating ?? 0) || 0;

const _getVisitors = (business) =>
  Number(business?.traffic_data?.metrics?.estimated_monthly_visitors ?? 0) || 0;

/**
 * Estimate a SpyFu-style domain strength score (0-100) for a business that
 * has NOT been enriched with SpyFu data. The estimate combines four
 * publicly available signals:
 *
 *   - Search rank position (0-40 points): the lower the rank, the
 *     stronger the SEO presence. Unranked / >100 contributes a small floor.
 *   - Review volume (0-25 points): more reviews implies more organic
 *     visibility, brand presence, and citation signal.
 *   - Average rating (0-15 points): higher rated pages tend to retain
 *     visitors and earn engagement signals search engines reward.
 *   - Traffic data presence (0-20 points): when any monthly visitor
 *     estimate exists, that's direct evidence of search visibility.
 *
 * @param {Object} business - Business object
 * @returns {number} - Estimated domain strength (5-95)
 */
export const estimateDomainStrength = (business) => {
  if (!business) return 5;

  let score = 0;

  const rank = _getRank(business);
  if (rank !== null) {
    if (rank <= 100) {
      score += Math.max(0, 40 - (rank - 1) * 0.5);
    } else {
      score += 6;
    }
  } else {
    score += 4;
  }

  const reviews = _getReviewCount(business);
  if (reviews >= 500) score += 25;
  else if (reviews >= 200) score += 20;
  else if (reviews >= 100) score += 15;
  else if (reviews >= 50) score += 10;
  else if (reviews >= 20) score += 6;
  else if (reviews > 0) score += Math.min(reviews / 5, 4);

  const rating = _getAvgRating(business);
  if (rating >= 4.7) score += 15;
  else if (rating >= 4.5) score += 12;
  else if (rating >= 4.0) score += 8;
  else if (rating >= 3.5) score += 4;
  else if (rating > 0) score += 1;

  const visitors = _getVisitors(business);
  if (visitors >= 10000) score += 20;
  else if (visitors >= 1000) score += 14;
  else if (visitors >= 100) score += 8;
  else if (visitors > 0) score += 4;

  return Math.round(_clamp(score, 5, 95));
};

/**
 * Return the SpyFu domain strength when available, otherwise return a
 * reasonable estimate derived from rank, reviews, rating, and traffic.
 * Use this anywhere the UI would otherwise show "N/A".
 *
 * @param {Object} business - Business object
 * @returns {number} - Domain strength 0-100
 */
export const getDomainStrengthOrEstimate = (business) => {
  if (hasCompetitiveIntel(business)) {
    const real = getDomainStrength(business);
    if (real > 0) return real;
  }
  return estimateDomainStrength(business);
};

/**
 * Whether the domain strength returned by getDomainStrengthOrEstimate for
 * this business is an estimate (i.e. SpyFu data was not available).
 * UIs can use this to display a subtle "est." marker.
 *
 * @param {Object} business - Business object
 * @returns {boolean}
 */
export const isDomainStrengthEstimated = (business) => {
  if (!hasCompetitiveIntel(business)) return true;
  return getDomainStrength(business) <= 0;
};

/**
 * Calculate the visitors/rank SEO score when we have direct traffic data,
 * otherwise estimate it from reviews + rating. The estimation treats
 * reviews and rating as proxies for monthly visibility:
 *
 *   estimatedVisitors = reviews * (3 .. 6) * ratingMultiplier
 *
 * where ratingMultiplier scales from 0.8 (3.0 stars) up to 1.4 (4.8+).
 * The final score is divided by the same rank-based divisor used by
 * `calculateSeoScore` so the units stay comparable.
 *
 * @param {Object} business - Business object
 * @returns {number} - Estimated SEO score (visitors/rank style units)
 */
export const estimateSeoScore = (business) => {
  if (!business) return 0;

  const reviews = _getReviewCount(business);
  const rating = _getAvgRating(business);

  let perReviewVisibility = 3;
  if (reviews >= 500) perReviewVisibility = 6;
  else if (reviews >= 200) perReviewVisibility = 5;
  else if (reviews >= 50) perReviewVisibility = 4;

  let ratingMultiplier = 1.0;
  if (rating >= 4.8) ratingMultiplier = 1.4;
  else if (rating >= 4.5) ratingMultiplier = 1.25;
  else if (rating >= 4.0) ratingMultiplier = 1.1;
  else if (rating >= 3.5) ratingMultiplier = 0.95;
  else if (rating > 0) ratingMultiplier = 0.8;

  const baselineFloor = reviews > 0 ? 12 : 6;
  const estimatedVisitors = Math.max(
    baselineFloor,
    reviews * perReviewVisibility * ratingMultiplier,
  );

  const rank = _getRank(business);
  const divisor = rank && rank > 0 && rank <= 100 ? rank : 100;

  return Math.round((estimatedVisitors / divisor) * 10) / 10;
};

/**
 * Return the real visitors/rank SEO score when traffic data is present;
 * otherwise return an estimate so the UI never has to render "N/A".
 *
 * @param {Object} business - Business object
 * @returns {number} - SEO score (real or estimated)
 */
export const calculateSeoScoreOrEstimate = (business) => {
  const real = calculateSeoScore(business);
  if (real > 0) return real;
  return estimateSeoScore(business);
};

/**
 * Whether the SEO score returned by calculateSeoScoreOrEstimate for this
 * business is an estimate (i.e. no traffic_data visitors were available).
 *
 * @param {Object} business - Business object
 * @returns {boolean}
 */
export const isSeoScoreEstimated = (business) => {
  return calculateSeoScore(business) <= 0;
};

/**
 * Sort a list of businesses by SEO Score so EVERY business gets a rank.
 *
 * Historically several dashboards computed `sortedBySeo` by calling
 * `calculateSeoScore` (which returns 0 when traffic_data is unavailable)
 * and then filtering out businesses with score 0. That meant any business
 * that wasn't enriched with SpyFu traffic data was excluded from SEO
 * rankings entirely, which propagated to consumers like BusinessReviewModal
 * and LocalyticsRankingCard as a literal "N/A" rank ordinal.
 *
 * This helper gives every business an SEO score using the estimate
 * fallback (rank + reviews + rating) when no real traffic data exists,
 * so the SEO category is always rankable. Each returned business is
 * decorated with three fields:
 *
 *   - seoScore: the real visitors/rank score, or 0 when unavailable
 *     (preserved so callers can choose to display "~est" markers)
 *   - seoScoreEstimated: the score actually used for sorting, never 0
 *   - seoScoreIsEstimate: true when the sort score was estimated
 *
 * @param {Array} businesses - businesses to sort
 * @returns {Array} businesses sorted by SEO score descending
 */
export const sortBusinessesBySeoScore = (businesses) => {
  if (!businesses || !businesses.length) return [];
  return businesses
    .map((business) => {
      const realScore = calculateSeoScore(business);
      const isEstimate = !(realScore > 0);
      const seoScoreEstimated = isEstimate
        ? estimateSeoScore(business)
        : realScore;
      return {
        ...business,
        seoScore: realScore,
        seoScoreValue: seoScoreEstimated,
        seoScoreEstimated,
        seoScoreIsEstimate: isEstimate,
      };
    })
    .sort(
      (a, b) => (b.seoScoreEstimated || 0) - (a.seoScoreEstimated || 0),
    );
};

/**
 * Calculate SEO Score growth based on traffic changes
 * Uses historical traffic data to calculate month-over-month SEO score change
 * @param {Object} business - Business object with traffic_data and query_ranking_info
 * @param {Array} trafficTimeSeries - Historical traffic time series data
 * @returns {Object} - { growth: number, percentChange: number, trend: 'up'|'down'|'stable' }
 */
export const calculateSeoScoreGrowth = (business, trafficTimeSeries) => {
  const currentScore = calculateSeoScore(business);
  
  if (!trafficTimeSeries?.length || trafficTimeSeries.length < 2) {
    return { growth: 0, percentChange: 0, trend: 'stable', currentScore };
  }
  
  // Sort by yearMonth descending
  const sorted = [...trafficTimeSeries].sort((a, b) => 
    b.yearMonth?.localeCompare(a.yearMonth)
  );
  
  // Get previous month's visitors
  const previousVisitors = sorted[1]?.organicClicks || 0;
  
  // Use same rank for both calculations (rank typically doesn't change month-to-month)
  const rankForSeo = business?.query_ranking_info?.url_ranking;
  const divisor = (typeof rankForSeo === 'number' && rankForSeo > 0 && rankForSeo <= 100) 
    ? rankForSeo 
    : 100;
  
  const previousScore = previousVisitors > 0 ? previousVisitors / divisor : 0;
  
  const growth = currentScore - previousScore;
  const percentChange = previousScore > 0 
    ? ((currentScore - previousScore) / previousScore) * 100 
    : (currentScore > 0 ? 100 : 0);
  
  let trend = 'stable';
  if (percentChange > 5) trend = 'up';
  else if (percentChange < -5) trend = 'down';
  
  return { 
    growth: Math.round(growth * 10) / 10, 
    percentChange: Math.round(percentChange * 10) / 10, 
    trend,
    currentScore 
  };
};

/**
 * Get color for keyword difficulty
 * @param {number} difficulty - Difficulty score (0-100)
 * @returns {string} - Chakra UI color scheme
 */
export const getKeywordDifficultyColor = (difficulty) => {
  if (difficulty >= 70) return 'red';
  if (difficulty >= 50) return 'orange';
  if (difficulty >= 30) return 'yellow';
  return 'green';
};

// ============================================================================
// ADS DATA HELPERS (domain_ad_history)
// ============================================================================

/**
 * Get historical ads for a business
 * @param {Object} business - Business object with spyfu_data
 * @returns {Array} - Array of historical ad objects
 */
export const getAdHistory = (business) => {
  if (!hasCompetitiveIntel(business)) return [];
  return business.spyfu_data?.domain_ad_history?.results || [];
};

/**
 * Get total number of ads for a business
 * @param {Object} business - Business object with spyfu_data
 * @returns {number} - Total ad count
 */
export const getTotalAds = (business) => {
  const ads = getAdHistory(business);
  return ads.length;
};

/**
 * Format ad count for display - shows "100+" when hitting API limit
 * @param {number} count - Ad count
 * @returns {string} - Formatted count string
 */
export const formatAdCount = (count) => {
  if (count >= 100) {
    return '100+';
  }
  return String(count);
};

/**
 * Rank businesses by total ad count.
 * Businesses with zero ads are placed at the end (still ranked, but with no ads).
 * @param {Array} businesses - Array of business objects
 * @returns {Array} - Ordered list of {business, mapsUri, name, adCount, rank}
 */
export const rankBusinessesByAdCount = (businesses = []) => {
  const enriched = (businesses || []).map((business) => ({
    business,
    mapsUri: business?.maps_uri || null,
    name: business?.name || '',
    adCount: getTotalAds(business),
  }));

  enriched.sort((a, b) => {
    if (b.adCount !== a.adCount) return b.adCount - a.adCount;
    return (a.name || '').localeCompare(b.name || '');
  });

  return enriched.map((entry, index) => ({
    ...entry,
    rank: index + 1,
  }));
};

/**
 * Compute the ad ranking for a single business within the market.
 *
 * Ranks across two cohorts:
 * - Among ALL provided businesses (overall market position)
 * - Among advertisers only (businesses with at least 1 ad)
 *
 * Also returns the market average ad count across both cohorts so callers
 * can show "X ads (#R of N, avg Y)" style comparisons.
 *
 * @param {Object} business - Business object with spyfu_data
 * @param {Array} allBusinesses - All businesses in the market
 * @returns {Object} - {
 *   adCount,
 *   rank, totalBusinesses,
 *   advertiserRank, totalAdvertisers,
 *   avgAdsAcrossMarket, avgAdsAcrossAdvertisers,
 *   isAdvertiser,
 * }
 */
export const getBusinessAdRank = (business, allBusinesses = []) => {
  const adCount = getTotalAds(business);
  const ranked = rankBusinessesByAdCount(allBusinesses);
  const totalBusinesses = ranked.length;
  const advertisers = ranked.filter((entry) => entry.adCount > 0);
  const totalAdvertisers = advertisers.length;

  const findRank = (list) => {
    if (!business?.maps_uri) return 0;
    const idx = list.findIndex((entry) => entry.mapsUri === business.maps_uri);
    return idx >= 0 ? idx + 1 : 0;
  };

  const totalAdsMarket = ranked.reduce((sum, entry) => sum + entry.adCount, 0);
  const totalAdsAdvertisers = advertisers.reduce(
    (sum, entry) => sum + entry.adCount,
    0,
  );

  return {
    adCount,
    rank: findRank(ranked),
    totalBusinesses,
    advertiserRank: findRank(advertisers),
    totalAdvertisers,
    avgAdsAcrossMarket:
      totalBusinesses > 0 ? totalAdsMarket / totalBusinesses : 0,
    avgAdsAcrossAdvertisers:
      totalAdvertisers > 0 ? totalAdsAdvertisers / totalAdvertisers : 0,
    isAdvertiser: adCount > 0,
  };
};

/**
 * Get all unique keywords targeted across all ads
 * @param {Object} business - Business object with spyfu_data
 * @returns {Array} - Array of unique keywords
 */
export const getAdKeywords = (business) => {
  const ads = getAdHistory(business);
  const keywords = new Set();
  ads.forEach(ad => {
    if (ad.keywords && Array.isArray(ad.keywords)) {
      ad.keywords.forEach(kw => keywords.add(kw));
    }
  });
  return Array.from(keywords);
};

/**
 * Get average ad position for a business
 * @param {Object} business - Business object with spyfu_data
 * @returns {number} - Average ad position
 */
export const getAvgAdPosition = (business) => {
  const ads = getAdHistory(business);
  if (ads.length === 0) return 0;
  
  const sum = ads.reduce((acc, ad) => acc + (ad.position || 0), 0);
  return sum / ads.length;
};

/**
 * Get most recent ad for a business
 * @param {Object} business - Business object with spyfu_data
 * @returns {Object|null} - Most recent ad or null
 */
export const getMostRecentAd = (business) => {
  const ads = getAdHistory(business);
  if (ads.length === 0) return null;
  
  return ads.reduce((latest, ad) => {
    if (!latest) return ad;
    return (ad.searchDateId || 0) > (latest.searchDateId || 0) ? ad : latest;
  }, null);
};

/**
 * Parse searchDateId to Date object
 * @param {number} searchDateId - Date in YYYYMMDD format
 * @returns {Date|null} - Parsed date or null
 */
export const parseSearchDate = (searchDateId) => {
  if (!searchDateId) return null;
  const str = String(searchDateId);
  const year = parseInt(str.substring(0, 4));
  const month = parseInt(str.substring(4, 6)) - 1; // JS months are 0-indexed
  const day = parseInt(str.substring(6, 8));
  return new Date(year, month, day);
};

/**
 * Format searchDateId to readable string
 * @param {number} searchDateId - Date in YYYYMMDD format
 * @returns {string} - Formatted date string
 */
export const formatSearchDate = (searchDateId) => {
  const date = parseSearchDate(searchDateId);
  if (!date) return 'Unknown';
  return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
};

/**
 * Group ads by keyword
 * @param {Array} businesses - Array of business objects
 * @returns {Object} - Object with keywords as keys and ad arrays as values
 */
export const groupAdsByKeyword = (businesses) => {
  const keywordMap = {};
  
  businesses.forEach(business => {
    const ads = getAdHistory(business);
    ads.forEach(ad => {
      if (ad.keywords && Array.isArray(ad.keywords)) {
        ad.keywords.forEach(keyword => {
          if (!keywordMap[keyword]) {
            keywordMap[keyword] = [];
          }
          keywordMap[keyword].push({
            ...ad,
            businessName: business.name,
            businessDomain: business.spyfu_data?.domain
          });
        });
      }
    });
  });
  
  return keywordMap;
};

/**
 * Get top keywords by ad count
 * @param {Array} businesses - Array of business objects
 * @param {number} limit - Number of keywords to return
 * @returns {Array} - Array of {keyword, count, businesses} objects
 */
export const getTopAdKeywords = (businesses, limit = 20) => {
  const keywordMap = groupAdsByKeyword(businesses);
  
  const keywordStats = Object.entries(keywordMap).map(([keyword, ads]) => {
    const uniqueBusinesses = new Set(ads.map(ad => ad.businessName));
    return {
      keyword,
      adCount: ads.length,
      businessCount: uniqueBusinesses.size,
      ads
    };
  });
  
  return keywordStats
    .sort((a, b) => b.adCount - a.adCount)
    .slice(0, limit);
};

/**
 * Get ad position distribution across market
 * @param {Array} businesses - Array of business objects
 * @returns {Object} - Object with positions as keys and counts as values
 */
export const getAdPositionDistribution = (businesses) => {
  const distribution = {};
  
  businesses.forEach(business => {
    const ads = getAdHistory(business);
    ads.forEach(ad => {
      const pos = ad.position || 'Unknown';
      distribution[pos] = (distribution[pos] || 0) + 1;
    });
  });
  
  return distribution;
};

/**
 * Get common call-to-action phrases from ad copy
 * @param {Array} businesses - Array of business objects
 * @returns {Array} - Array of common phrases
 */
export const getCommonCTAs = (businesses) => {
  const ctaPhrases = [
    'call', 'book', 'save', 'contact', 'get', 'free', 'today', 'now',
    'schedule', 'request', 'order', 'shop', 'learn', 'discover', 'try'
  ];

  const ctaCounts = {};

  businesses.forEach(business => {
    const ads = getAdHistory(business);
    ads.forEach(ad => {
      const text = `${ad.title || ''} ${ad.body || ''}`.toLowerCase();
      ctaPhrases.forEach(phrase => {
        if (text.includes(phrase)) {
          ctaCounts[phrase] = (ctaCounts[phrase] || 0) + 1;
        }
      });
    });
  });

  return Object.entries(ctaCounts)
    .sort((a, b) => b[1] - a[1])
    .map(([phrase, count]) => ({ phrase, count }));
};

// ============================================================================
// AD SUCCESS SCORING HELPERS
// ============================================================================

// Scoring weights
const POSITION_WEIGHT = 0.40;
const LONGEVITY_WEIGHT = 0.35;
const KEYWORD_COVERAGE_WEIGHT = 0.25;

// Thresholds
const SUCCESSFUL_THRESHOLD = 60;
const HIGHLY_SUCCESSFUL_THRESHOLD = 80;

/**
 * Calculate position score (0-100) based on average ad position
 * Position 1 = 100, Position 2 = 85, etc.
 * @param {number} avgPosition - Average ad position
 * @returns {number} - Score between 0 and 100
 */
export const calculatePositionScore = (avgPosition) => {
  if (!avgPosition || avgPosition <= 0) return 0;
  return Math.max(0, 100 - (avgPosition - 1) * 15);
};

/**
 * Calculate longevity score (0-100) based on ad persistence
 * Each unique appearance date adds 20 points, capped at 100
 * @param {number} uniqueDates - Number of unique dates ad appeared
 * @returns {number} - Score between 0 and 100
 */
export const calculateLongevityScore = (uniqueDates) => {
  if (!uniqueDates || uniqueDates <= 0) return 0;
  return Math.min(100, uniqueDates * 20);
};

/**
 * Calculate keyword coverage score (0-100) based on keyword reach
 * Each unique keyword adds 10 points, capped at 100
 * @param {number} uniqueKeywords - Number of unique keywords
 * @returns {number} - Score between 0 and 100
 */
export const calculateKeywordCoverageScore = (uniqueKeywords) => {
  if (!uniqueKeywords || uniqueKeywords <= 0) return 0;
  return Math.min(100, uniqueKeywords * 10);
};

/**
 * Calculate total weighted success score
 * @param {number} positionScore - Position component (0-100)
 * @param {number} longevityScore - Longevity component (0-100)
 * @param {number} keywordScore - Keyword coverage component (0-100)
 * @returns {number} - Weighted total score (0-100)
 */
export const calculateTotalScore = (positionScore, longevityScore, keywordScore) => {
  return (
    positionScore * POSITION_WEIGHT +
    longevityScore * LONGEVITY_WEIGHT +
    keywordScore * KEYWORD_COVERAGE_WEIGHT
  );
};

/**
 * Calculate success score for an ad
 * @param {Object} ad - Ad object with position, keywords, searchDateId
 * @param {Array} allAds - All ads for context (to calculate longevity)
 * @returns {Object} - Success score breakdown
 */
export const calculateAdSuccessScore = (ad, allAds = []) => {
  const position = ad.position || 10;
  const keywords = ad.keywords || [];

  // For individual ads, longevity is based on how many times same title/body appears
  const adKey = `${(ad.title || '').toLowerCase()}|||${(ad.body || '').toLowerCase()}`;
  const sameAds = allAds.filter(a => {
    const key = `${(a.title || '').toLowerCase()}|||${(a.body || '').toLowerCase()}`;
    return key === adKey;
  });
  const uniqueDates = new Set(sameAds.map(a => a.searchDateId).filter(Boolean)).size;

  const positionScore = calculatePositionScore(position);
  const longevityScore = calculateLongevityScore(uniqueDates || 1);
  const keywordCoverageScore = calculateKeywordCoverageScore(keywords.length);
  const totalScore = calculateTotalScore(positionScore, longevityScore, keywordCoverageScore);

  return {
    positionScore: Math.round(positionScore * 10) / 10,
    longevityScore: Math.round(longevityScore * 10) / 10,
    keywordCoverageScore: Math.round(keywordCoverageScore * 10) / 10,
    totalScore: Math.round(totalScore * 10) / 10,
    isSuccessful: totalScore >= SUCCESSFUL_THRESHOLD,
    isHighlySuccessful: totalScore >= HIGHLY_SUCCESSFUL_THRESHOLD,
  };
};

/**
 * Filter ads to only include successful ones
 * @param {Array} ads - Array of ads
 * @param {number} minScore - Minimum success score (default: 60)
 * @returns {Array} - Filtered ads with success scores attached
 */
export const filterSuccessfulAds = (ads, minScore = SUCCESSFUL_THRESHOLD) => {
  if (!ads || ads.length === 0) return [];

  return ads
    .map(ad => ({
      ...ad,
      successScore: calculateAdSuccessScore(ad, ads),
    }))
    .filter(ad => ad.successScore.totalScore >= minScore)
    .sort((a, b) => b.successScore.totalScore - a.successScore.totalScore);
};

/**
 * Get color for success score (Chakra UI color scheme)
 * @param {number} score - Success score (0-100)
 * @returns {string} - Chakra UI color scheme
 */
export const getSuccessScoreColor = (score) => {
  if (score >= HIGHLY_SUCCESSFUL_THRESHOLD) return 'green';
  if (score >= SUCCESSFUL_THRESHOLD) return 'blue';
  if (score >= 40) return 'yellow';
  return 'gray';
};

/**
 * Get badge label for success score
 * @param {number} score - Success score (0-100)
 * @returns {string} - Label text
 */
export const getSuccessScoreLabel = (score) => {
  if (score >= HIGHLY_SUCCESSFUL_THRESHOLD) return 'Highly Successful';
  if (score >= SUCCESSFUL_THRESHOLD) return 'Successful';
  if (score >= 40) return 'Moderate';
  return 'Low Performance';
};

/**
 * Score all ads from businesses and return summary
 * @param {Array} businesses - Array of business objects
 * @returns {Object} - Summary with scored ads and statistics
 */
export const scoreAllAds = (businesses) => {
  const allAds = [];

  // Collect all ads with business context
  businesses.forEach(business => {
    const ads = getAdHistory(business);
    ads.forEach(ad => {
      allAds.push({
        ...ad,
        businessName: business.name,
        businessDomain: business.spyfu_data?.domain,
      });
    });
  });

  if (allAds.length === 0) {
    return {
      totalAds: 0,
      successfulAds: 0,
      highlySuccessfulAds: 0,
      avgSuccessScore: 0,
      scoredAds: [],
    };
  }

  // Score all ads
  const scoredAds = allAds.map(ad => ({
    ...ad,
    successScore: calculateAdSuccessScore(ad, allAds),
  }));

  // Sort by score descending
  scoredAds.sort((a, b) => b.successScore.totalScore - a.successScore.totalScore);

  // Calculate summary stats
  const successfulAds = scoredAds.filter(a => a.successScore.isSuccessful).length;
  const highlySuccessfulAds = scoredAds.filter(a => a.successScore.isHighlySuccessful).length;
  const avgSuccessScore = scoredAds.reduce((sum, a) => sum + a.successScore.totalScore, 0) / scoredAds.length;

  return {
    totalAds: scoredAds.length,
    successfulAds,
    highlySuccessfulAds,
    avgSuccessScore: Math.round(avgSuccessScore * 10) / 10,
    scoredAds,
  };
};

// ============================================================================
// HISTORICAL TRAFFIC + REVIEWS HELPERS
// ============================================================================

/**
 * Format yearMonth string to display label
 * @param {string} yearMonth - Year-month in "YYYY-MM" format
 * @returns {string} - Formatted display string (e.g., "Nov 2025")
 */
export const formatYearMonth = (yearMonth) => {
  if (!yearMonth) return '';
  const [year, month] = yearMonth.split('-');
  const date = new Date(parseInt(year), parseInt(month) - 1);
  return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
};

/**
 * Get historical traffic data from a business object (from /historical-traffic endpoint)
 * @param {Object} businessData - Business data from historical-traffic endpoint
 * @returns {Array} - Array of time series data points
 */
export const getHistoricalTrafficData = (businessData) => {
  if (!businessData?.timeSeries) return [];
  return businessData.timeSeries;
};

/**
 * Filter time series data to a specific date range
 * @param {Array} timeSeries - Array of time series data points
 * @param {string} startYearMonth - Start date in "YYYY-MM" format
 * @param {string} endYearMonth - End date in "YYYY-MM" format
 * @returns {Array} - Filtered time series
 */
export const filterTimeSeriesByDateRange = (timeSeries, startYearMonth, endYearMonth) => {
  if (!timeSeries?.length) return [];
  
  return timeSeries.filter(point => {
    const ym = point.yearMonth;
    if (!ym) return false;
    if (startYearMonth && ym < startYearMonth) return false;
    if (endYearMonth && ym > endYearMonth) return false;
    return true;
  });
};

/**
 * Get the most recent N months of data
 * @param {Array} timeSeries - Array of time series data points
 * @param {number} months - Number of months to return
 * @returns {Array} - Most recent N months of data
 */
export const getRecentMonths = (timeSeries, months = 12) => {
  if (!timeSeries?.length) return [];
  
  // Sort by yearMonth descending and take the most recent
  const sorted = [...timeSeries].sort((a, b) => {
    if (a.yearMonth > b.yearMonth) return -1;
    if (a.yearMonth < b.yearMonth) return 1;
    return 0;
  });
  
  // Take most recent N months, then reverse to get chronological order
  return sorted.slice(0, months).reverse();
};

/**
 * Calculate month-over-month change for a metric
 * @param {Array} timeSeries - Array of time series data points
 * @param {string} metric - Metric name (e.g., 'organicClicks', 'strength')
 * @returns {Object} - { change: number, percentChange: number, trend: 'up'|'down'|'stable' }
 */
export const calculateMoMChange = (timeSeries, metric) => {
  if (!timeSeries?.length || timeSeries.length < 2) {
    return { change: 0, percentChange: 0, trend: 'stable' };
  }
  
  // Get the two most recent months
  const sorted = [...timeSeries].sort((a, b) => b.yearMonth.localeCompare(a.yearMonth));
  const current = sorted[0]?.[metric] || 0;
  const previous = sorted[1]?.[metric] || 0;
  
  const change = current - previous;
  const percentChange = previous !== 0 ? (change / previous) * 100 : 0;
  
  let trend = 'stable';
  if (percentChange > 5) trend = 'up';
  else if (percentChange < -5) trend = 'down';
  
  return { change, percentChange: Math.round(percentChange * 10) / 10, trend };
};

/**
 * Calculate correlation between traffic and reviews
 * @param {Array} timeSeries - Array of time series data points
 * @returns {number} - Correlation coefficient (-1 to 1)
 */
export const calculateTrafficReviewCorrelation = (timeSeries) => {
  if (!timeSeries?.length || timeSeries.length < 3) return 0;
  
  const data = timeSeries.filter(p => p.organicClicks > 0 && p.reviewCount > 0);
  if (data.length < 3) return 0;
  
  const n = data.length;
  const traffic = data.map(p => p.organicClicks);
  const reviews = data.map(p => p.reviewCount);
  
  const meanTraffic = traffic.reduce((a, b) => a + b, 0) / n;
  const meanReviews = reviews.reduce((a, b) => a + b, 0) / n;
  
  let numerator = 0;
  let denomTraffic = 0;
  let denomReviews = 0;
  
  for (let i = 0; i < n; i++) {
    const diffTraffic = traffic[i] - meanTraffic;
    const diffReviews = reviews[i] - meanReviews;
    numerator += diffTraffic * diffReviews;
    denomTraffic += diffTraffic * diffTraffic;
    denomReviews += diffReviews * diffReviews;
  }
  
  const denominator = Math.sqrt(denomTraffic * denomReviews);
  if (denominator === 0) return 0;
  
  return Math.round((numerator / denominator) * 100) / 100;
};

/**
 * Get summary statistics for a business's historical data
 * @param {Object} businessData - Business data from historical-traffic endpoint
 * @returns {Object} - Summary statistics
 */
export const getHistoricalSummary = (businessData) => {
  const timeSeries = getHistoricalTrafficData(businessData);
  const recentData = getRecentMonths(timeSeries, 12);
  
  if (!recentData.length) {
    return {
      hasData: false,
      dataMonths: 0,
    };
  }
  
  // Aggregate metrics
  const avgOrganicClicks = Math.round(
    recentData.reduce((sum, p) => sum + (p.organicClicks || 0), 0) / recentData.length
  );
  const avgOrganicValue = Math.round(
    recentData.reduce((sum, p) => sum + (p.organicValue || 0), 0) / recentData.length
  );
  const avgStrength = Math.round(
    recentData.reduce((sum, p) => sum + (p.strength || 0), 0) / recentData.length
  );
  const totalReviews = recentData.reduce((sum, p) => sum + (p.reviewCount || 0), 0);
  
  // Get latest values
  const latest = recentData[recentData.length - 1] || {};
  
  // Calculate trends
  const clicksTrend = calculateMoMChange(recentData, 'organicClicks');
  const strengthTrend = calculateMoMChange(recentData, 'strength');
  const reviewsTrend = calculateMoMChange(recentData, 'reviewCount');
  
  return {
    hasData: true,
    dataMonths: recentData.length,
    latestMonth: latest.yearMonth,
    // Current values
    currentOrganicClicks: latest.organicClicks || 0,
    currentOrganicValue: latest.organicValue || 0,
    currentStrength: latest.strength || 0,
    currentReviewCount: latest.reviewCount || 0,
    // Averages
    avgOrganicClicks,
    avgOrganicValue,
    avgStrength,
    totalReviewsLast12Mo: totalReviews,
    // Trends
    clicksTrend,
    strengthTrend,
    reviewsTrend,
    // Correlation
    trafficReviewCorrelation: calculateTrafficReviewCorrelation(recentData),
  };
};

/**
 * Prepare chart data for multi-line visualization
 * @param {Object} businessData - Business data from historical-traffic endpoint
 * @param {Object} options - Options for data preparation
 * @returns {Array} - Array of data points ready for Recharts
 */
export const prepareChartData = (businessData, options = {}) => {
  const { months = 24 } = options;
  const timeSeries = getHistoricalTrafficData(businessData);
  const data = getRecentMonths(timeSeries, months);
  
  return data.map(point => ({
    ...point,
    month: formatYearMonth(point.yearMonth),
    // Ensure all numeric fields are numbers
    organicClicks: point.organicClicks || 0,
    organicValue: point.organicValue || 0,
    strength: point.strength || 0,
    reviewCount: point.reviewCount || 0,
    averageRating: point.averageRating || 0,
    positiveReviews: point.positiveReviews || 0,
    negativeReviews: point.negativeReviews || 0,
  }));
};

/**
 * Compare multiple businesses by a specific metric over time
 * @param {Array} businessesData - Array of business data from historical-traffic endpoint
 * @param {string} metric - Metric to compare
 * @param {number} months - Number of months to include
 * @returns {Array} - Array suitable for multi-line chart
 */
export const compareBusinessesByMetric = (businessesData, metric = 'organicClicks', months = 12) => {
  // Collect all unique yearMonths
  const allMonths = new Set();
  businessesData.forEach(b => {
    const timeSeries = getHistoricalTrafficData(b);
    timeSeries.forEach(p => allMonths.add(p.yearMonth));
  });
  
  // Sort months and take most recent N
  const sortedMonths = [...allMonths].sort().slice(-months);
  
  // Build comparison data
  return sortedMonths.map(ym => {
    const row = { yearMonth: ym, month: formatYearMonth(ym) };
    
    businessesData.forEach(b => {
      const timeSeries = getHistoricalTrafficData(b);
      const point = timeSeries.find(p => p.yearMonth === ym);
      row[b.name] = point?.[metric] || 0;
    });
    
    return row;
  });
};
